/**
 * CR-5861: Approvals Tab - Displays supplier accreditations from the Approval Register.
 *
 * Uses the same data source as the Quality > Approval Register page
 * (SupplierAccreditation model) to ensure consistency between the two views.
 * Grouped by Approval Group (e.g., "Quality", "Environmental", "Aerospace").
 * Supports certificate file download and click-through detail modal.
 */

import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Loader2,
  ShieldCheck,
  AlertTriangle,
  Download,
  CheckCircle2,
  XCircle,
  Calendar,
  Hash,
  FileText,
  Clock,
  Eye,
  Award,
  HelpCircle,
} from 'lucide-react';
import { qualityAPI, type SupplierAccreditation, type AccreditationStatus } from '../../lib/api';
import {
  NO_EXPIRY_DELIBERATE_LABEL,
  NO_EXPIRY_RECORDED_LABEL,
} from '../ApprovalRegisterCell';
import { useFormatters } from '../../contexts/PreferencesContext';
import { toast } from 'sonner';

interface ApprovalsTabProps {
  /**
   * CR-6247: the Company id whose accreditations to show. Was `supplier:
   * SupplierProfile`, but only ``supplier.company`` was ever read — taking the
   * id directly lets the Company Profile page reuse this tab unchanged.
   */
  companyId: number;
  /** Noun used in the empty state, e.g. "supplier" or "company". */
  subjectLabel?: string;
}

/**
 * Returns status info with color class and label based on status_code.
 *
 * CR-7171: `no_expiry` used to share the `in_date` branch and was therefore
 * asserted as "Valid" — a claim the record cannot support, because no expiry
 * date was ever recorded against it. It now states what is actually known.
 */
function getStatusInfo(statusCode: AccreditationStatus): { color: string; bgColor: string; label: string } {
  switch (statusCode) {
    case 'expired':
      return { color: 'text-red-600', bgColor: 'bg-red-100 dark:bg-red-900/30', label: 'Expired' };
    case 'expiring':
      return { color: 'text-amber-600', bgColor: 'bg-amber-100 dark:bg-amber-900/30', label: 'Expiring Soon' };
    case 'no_expiry':
      return {
        color: 'text-slate-600 dark:text-slate-300',
        bgColor: 'bg-slate-100 dark:bg-slate-800/40',
        label: NO_EXPIRY_RECORDED_LABEL,
      };
    case 'in_date':
    default:
      return { color: 'text-green-600', bgColor: 'bg-green-100 dark:bg-green-900/30', label: 'Valid' };
  }
}

/**
 * Download or view a certificate file.
 * Uses the direct certificate_file_url from the API response.
 */
function handleCertificateAction(
  url: string,
  filename: string,
  action: 'view' | 'download'
): void {
  try {
    if (action === 'view') {
      window.open(url, '_blank');
    } else {
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  } catch (err: unknown) {
    console.error('Failed to handle certificate:', err);
    toast.error('Failed to access certificate file');
  }
}

export function ApprovalsTab({ companyId, subjectLabel = 'supplier' }: ApprovalsTabProps) {
  const { formatDate } = useFormatters();
  const [accreditations, setAccreditations] = useState<SupplierAccreditation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedAccreditation, setSelectedAccreditation] = useState<SupplierAccreditation | null>(null);

  useEffect(() => {
    fetchAccreditations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const fetchAccreditations = async () => {
    try {
      setLoading(true);
      setError(null);
      // CR-5861: Fetch from Quality Approval Register (same source as Approval Register page)
      const res = await qualityAPI.getSupplierAccreditations({ supplier: companyId });
      setAccreditations(res.data.results || []);
    } catch (err: unknown) {
      console.error('Failed to fetch accreditations:', err);
      setError('Failed to load approvals');
    } finally {
      setLoading(false);
    }
  };

  // Group by Approval Group (e.g., "Quality", "Environmental", "Aerospace")
  const grouped = accreditations.reduce<Record<string, SupplierAccreditation[]>>((acc, item) => {
    const key = item.approval_group_name || 'Other';
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});

  const groupKeys = Object.keys(grouped).sort();

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5" />
            Approvals
            {!loading && (
              <Badge variant="outline" className="ml-2">
                {accreditations.length} {accreditations.length === 1 ? 'accreditation' : 'accreditations'}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : error ? (
            <div className="text-center py-12 text-red-600">
              <p>{error}</p>
              <button
                onClick={fetchAccreditations}
                className="mt-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
              >
                Retry
              </button>
            </div>
          ) : accreditations.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <ShieldCheck className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No approvals have been recorded for this {subjectLabel}.</p>
            </div>
          ) : (
            <div className="space-y-8">
              {groupKeys.map((groupLabel) => {
                const items = grouped[groupLabel];
                return (
                  <div key={groupLabel}>
                    <div className="mb-4 px-4 py-2 bg-primary/5 border border-primary/20 rounded-lg">
                      <h3 className="text-base font-semibold">{groupLabel}</h3>
                    </div>
                    <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                      {items.map((item) => (
                        <AccreditationCard
                          key={item.id}
                          accreditation={item}
                          formatDate={formatDate}
                          onClick={() => setSelectedAccreditation(item)}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Modal */}
      <AccreditationDetailModal
        accreditation={selectedAccreditation}
        open={!!selectedAccreditation}
        onClose={() => setSelectedAccreditation(null)}
        formatDate={formatDate}
      />
    </>
  );
}

interface AccreditationCardProps {
  accreditation: SupplierAccreditation;
  formatDate: (d: string) => string;
  onClick: () => void;
}

function AccreditationCard({ accreditation, formatDate, onClick }: AccreditationCardProps) {
  const hasFile = !!accreditation.certificate_file_url;
  const status = getStatusInfo(accreditation.status_code);
  const isExpired = accreditation.status_code === 'expired';
  const isExpiring = accreditation.status_code === 'expiring';
  // CR-7171: a tick icon asserts the same thing the old "Valid" label did.
  const isUndated = accreditation.status_code === 'no_expiry';
  const statusLabel =
    isUndated && accreditation.no_expiry_applies
      ? NO_EXPIRY_DELIBERATE_LABEL
      : status.label;

  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!hasFile || !accreditation.certificate_file_url) return;
    handleCertificateAction(
      accreditation.certificate_file_url,
      accreditation.certificate_file_name || 'certificate',
      'download'
    );
  };

  return (
    <Card
      className="flex flex-col justify-between cursor-pointer hover:shadow-md transition-shadow group"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onClick()}
    >
      <CardContent className="pt-6 flex flex-col items-center text-center gap-3">
        {/* Status indicator icon */}
        <div className={`w-20 h-20 rounded-full ${status.bgColor} flex items-center justify-center relative`}>
          <Award className={`h-10 w-10 ${status.color}`} />
          {/* Click indicator */}
          <div className="absolute -top-1 -right-1 bg-primary text-primary-foreground rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <Eye className="h-3 w-3" />
          </div>
        </div>

        {/* Accreditation name */}
        <h4 className="font-medium text-sm">{accreditation.accreditation_name}</h4>

        {/* Approval Group */}
        {accreditation.approval_group_name && (
          <p className="text-xs text-muted-foreground">{accreditation.approval_group_name}</p>
        )}

        {/* Certificate number */}
        {accreditation.certificate_number && (
          <p className="text-xs text-muted-foreground">#{accreditation.certificate_number}</p>
        )}

        {/* Status badge */}
        <Badge className={`${status.bgColor} ${status.color} border-0`}>
          {isExpired || isExpiring ? (
            <><AlertTriangle className="h-3 w-3 mr-1" /> {statusLabel}</>
          ) : isUndated ? (
            <><HelpCircle className="h-3 w-3 mr-1" /> {statusLabel}</>
          ) : (
            <><CheckCircle2 className="h-3 w-3 mr-1" /> {statusLabel}</>
          )}
        </Badge>

        {/* Expiry date */}
        {accreditation.expiry_date ? (
          <p className={`text-xs font-medium ${status.color}`}>
            {isExpired ? (
              <span className="flex items-center gap-1">
                Expired: {formatDate(accreditation.expiry_date)}
              </span>
            ) : (
              <>Expiry: {formatDate(accreditation.expiry_date)}</>
            )}
          </p>
        ) : (
          <p className={`text-xs font-medium ${status.color}`}>
            {accreditation.no_expiry_applies
              ? `Expiry: ${NO_EXPIRY_DELIBERATE_LABEL}`
              : 'Expiry: Not recorded'}
          </p>
        )}

        {/* Last Updated Date */}
        {accreditation.updated_at && (
          <p className="text-xs text-muted-foreground flex items-center gap-1">
            <Clock className="h-3 w-3" />
            Updated: {formatDate(accreditation.updated_at)}
          </p>
        )}

        {/* Certificate attachment */}
        {hasFile ? (
          <button
            onClick={handleDownload}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-primary/10 text-primary rounded hover:bg-primary/20 transition-colors"
          >
            <Download className="h-3 w-3" />
            Certificate
          </button>
        ) : (
          <span className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-red-50 text-red-600 dark:bg-red-900/30 rounded">
            <XCircle className="h-3 w-3" />
            Certificate - Not Attached
          </span>
        )}
      </CardContent>
    </Card>
  );
}

interface AccreditationDetailModalProps {
  accreditation: SupplierAccreditation | null;
  open: boolean;
  onClose: () => void;
  formatDate: (d: string) => string;
}

function AccreditationDetailModal({ accreditation, open, onClose, formatDate }: AccreditationDetailModalProps) {
  if (!accreditation) return null;

  const status = getStatusInfo(accreditation.status_code);
  const hasFile = !!accreditation.certificate_file_url;

  const handleView = () => {
    if (!hasFile || !accreditation.certificate_file_url) return;
    handleCertificateAction(
      accreditation.certificate_file_url,
      accreditation.certificate_file_name || 'certificate',
      'view'
    );
  };

  const handleDownload = () => {
    if (!hasFile || !accreditation.certificate_file_url) return;
    handleCertificateAction(
      accreditation.certificate_file_url,
      accreditation.certificate_file_name || 'certificate',
      'download'
    );
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Award className={`h-5 w-5 ${status.color}`} />
            Approval Details
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Status Banner */}
          <div className={`${status.bgColor} ${status.color} rounded-lg p-3 flex items-center justify-between`}>
            <span className="font-medium">{status.label}</span>
          </div>

          {/* Detail Grid */}
          <div className="grid grid-cols-2 gap-4">
            {/* Approval Type (Approval Group) */}
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <FileText className="h-3 w-3" /> Approval Type
              </p>
              <p className="text-sm font-medium">
                {accreditation.approval_group_name || 'General'}
              </p>
            </div>

            {/* Accreditation Name */}
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Award className="h-3 w-3" /> Accreditation Name
              </p>
              <p className="text-sm font-medium">{accreditation.accreditation_name}</p>
            </div>

            {/* Approval Number */}
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Hash className="h-3 w-3" /> Approval Number
              </p>
              <p className="text-sm font-medium">{accreditation.certificate_number || 'N/A'}</p>
            </div>

            {/* Issue Date */}
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Calendar className="h-3 w-3" /> Issue Date
              </p>
              <p className="text-sm font-medium">
                {accreditation.issued_date ? formatDate(accreditation.issued_date) : 'N/A'}
              </p>
            </div>

            {/* Expiry Date */}
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Calendar className="h-3 w-3" /> Expiry Date
              </p>
              <p className={`text-sm font-medium ${status.color}`}>
                {accreditation.expiry_date
                  ? formatDate(accreditation.expiry_date)
                  : accreditation.no_expiry_applies
                    ? NO_EXPIRY_DELIBERATE_LABEL
                    : 'Not recorded'}
              </p>
            </div>

            {/* Last Updated */}
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Clock className="h-3 w-3" /> Last Updated
              </p>
              <p className="text-sm font-medium">{formatDate(accreditation.updated_at)}</p>
            </div>

            {/* Created By */}
            {accreditation.created_by_name && (
              <div className="space-y-1 col-span-2">
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <FileText className="h-3 w-3" /> Created By
                </p>
                <p className="text-sm font-medium">{accreditation.created_by_name}</p>
              </div>
            )}

            {/* Notes */}
            {accreditation.notes && (
              <div className="space-y-1 col-span-2">
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <FileText className="h-3 w-3" /> Notes
                </p>
                <p className="text-sm">{accreditation.notes}</p>
              </div>
            )}
          </div>

          {/* Certificate Attachment */}
          <div className="border-t pt-4">
            <p className="text-xs text-muted-foreground mb-2">Certificate Attachment</p>
            {hasFile ? (
              <div className="flex gap-2">
                <button
                  onClick={handleView}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                >
                  <Eye className="h-4 w-4" />
                  View Certificate
                </button>
                <button
                  onClick={handleDownload}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/80 transition-colors"
                >
                  <Download className="h-4 w-4" />
                  Download
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 px-4 py-3 bg-red-50 dark:bg-red-900/20 text-red-600 rounded-lg">
                <XCircle className="h-5 w-5" />
                <span className="text-sm font-medium">No certificate file attached</span>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
